// domain/constants/credentialingBodies.js

/**
 * The bodies that issue the credentials a clinic records, and what each one issues.
 *
 * THIS CONTEXT'S VOCABULARY, which is why it lives here rather than where it was. It spent years as a
 * data block inside `mixins/Credentialing.js`, a deprecated mixin whose other 1500 lines are BACB
 * task-list content and have nothing to do with it. The mixin now reads it from here, so its ten
 * consumers are unchanged and there is one copy rather than two drifting.
 *
 * STATIC, NOT CONFIGURED. Nobody adds a certification board through the product: these are the real
 * bodies, and a clinic recording a credential picks one. A body missing from this list is a change
 * here, deliberately, because the levels below are what a payer's rules are written against.
 *
 * `levels` EMPTY MEANS THE BODY ISSUES ONE THING, so the record carries a number and no level — a
 * state license is a license. It does not mean the levels are unknown.
 *
 * `numberLabelKey` NAMES THE NUMBER IN THE ISSUER'S OWN WORDS. A state grants a license and a board
 * grants a certificate, and asking for a "certificate number" on a license is asking for something
 * the document in front of the reader does not call itself. It is a dictionary key rather than a
 * literal, because these are words a clinician reads.
 *
 * STATE LICENSURE IS ONE BODY WITH TWO LEVELS, not one body per state. The title differs by state —
 * LBA for a behavior analyst, LABA for an assistant — and the state practice-law sheet describes the
 * same two tiers, so the level says which tier and the jurisdiction says which state. Fifty bodies
 * would say the same thing far less usefully.
 *
 * `hasJurisdiction` MEANS THE ISSUER IS A STATE RATHER THAN A NATIONAL BODY. A behavior-analyst
 * license is granted per state, and one person can hold several at once: the paper file writes the
 * issuer as "State A board" rather than as a single national authority. Without the state recorded,
 * two licenses are indistinguishable in a list, and two states issuing the same number would read as
 * one certificate renewed — which is why the state is part of a license's identity and not a note.
 *
 * A BACB REGISTRATION NUMBER USED TO SIT HERE and is gone, along with its two import columns. It
 * existed so the number could be printed on RBT training diplomas, which this product does not
 * produce, and confirmation came back that no clinic ever recorded one. Nothing read it either: not
 * the store, not the RBT competency checklist, not reporting, and not the backend, which handles the
 * credential array without knowing one body from another. With no stored data to strand it could go
 * outright rather than being deprecated, which is the difference from the NPI below.
 *
 * `nppesNpi` IS DEPRECATED RATHER THAN REMOVED. An NPI is an identifier rather than a qualification:
 * it has no issuer that grants or withdraws it, nothing expires, and revenueCycle already owns the
 * number as a field of its own on this same record. But it cannot simply go, because it is wired
 * into more than it looks:
 *
 *   - It generates two selectable COLUMNS (`nppesNpi`, `nppesNpi_taxonomy`) in four tables — the
 *     staff list, appointments, session activities and the user picker — and a saved view can name
 *     them.
 *   - The staff IMPORT maps `nppesNpiNPI` and `nppesNpiTaxonomy` onto it.
 *   - Whatever clinics have already recorded under it lives in `credentials[]` and would stop
 *     rendering anywhere.
 *
 * So it stays readable and sorts LAST, marked, and nobody reaches for it by accident. Retiring it
 * properly means moving the number to the field revenueCycle owns and the taxonomy to the list this
 * context now holds, which is migration work rather than a constant edit.
 */
export const CREDENTIALING_BODIES = {
  bacb: {
    // Just "BACB". The "(Certification)" half existed only to tell it apart from a registration
    // number entry that no longer exists.
    name: 'BACB',
    levels: ['BCBA-D', 'BCBA', 'BCaBA', 'RBT'],
    hasExpirationDate: true,
  },
  nppesNpi: {
    name: 'NPPES NPI Registry',
    levels: [],
    hasTaxonomy: true,
    hasExpirationDate: false,
    // Read by the staff-list columns in the legacy credentialing mixin, which is its only consumer.
    certificationLabel: 'NPI',
    numberLabelKey: 'inputNpiNumber',
    isDeprecated: true,
  },
  // The KEY stays `lbaUS` although the name no longer says LBA: every credential already recorded
  // names this body by that key, and renaming it would orphan them. What a body is CALLED is display;
  // its key is a contract with stored data.
  lbaUS: {
    name: 'State licensure',
    levels: ['LBA', 'LABA'],
    hasExpirationDate: true,
    hasJurisdiction: true,
    numberLabelKey: 'inputLicenseNumber',
  },
  ibao: {
    name: 'IBAO',
    levels: ['IBA', 'IBT'],
    hasExpirationDate: true,
  },
  qaba: {
    name: 'QABA',
    levels: ['QBA', 'QASP-S', 'ABAT'],
    hasExpirationDate: true,
  },
  // Licensure OUTSIDE behavior analysis, kept apart from `lbaUS` because the two answer different
  // questions. `lbaUS` is the behavior-analyst license a state grants on top of a BACB certification;
  // this is the license somebody practises under in their OWN profession. Many states let other
  // licensed professionals deliver and oversee ABA within their scope without holding a BCBA at all,
  // and some payers accept them at the top tier, so a person can qualify through this body alone.
  //
  // THE LEVELS RECORD WHAT SOMEBODY HOLDS, NEVER WHAT A PAYER ACCEPTS. Which of these earn which
  // modifier is a payer's rule and is authored per payer; listing a profession here asserts only that
  // it is a license a staff member can hold.
  otherProfessionalLicense: {
    name: 'Professional licensure',
    levels: [
      'Licensed Psychologist',
      'Licensed Psychological Associate',
      'Licensed Clinical Social Worker',
      'Licensed Professional Counselor',
      'Licensed Marriage and Family Therapist',
      'Licensed Speech-Language Pathologist',
      'Licensed Occupational Therapist',
    ],
    hasExpirationDate: true,
    hasJurisdiction: true,
    numberLabelKey: 'inputLicenseNumber',
  },
};

/** What a body issues. Empty when the body issues exactly one thing, never "unknown". */
export const bodyLevels = (bodyId) => CREDENTIALING_BODIES[bodyId]?.levels ?? [];

/** Is this body one nobody should be reaching for on a new record? */
export const isDeprecatedBody = (bodyId) => CREDENTIALING_BODIES[bodyId]?.isDeprecated === true;

/** Does this body issue something that lapses? A license does; a registration number does not. */
export const bodyHasExpiry = (bodyId) => CREDENTIALING_BODIES[bodyId]?.hasExpirationDate === true;

/** Is this body a state rather than a national one, so a license needs the state recorded? */
export const bodyHasJurisdiction = (bodyId) => CREDENTIALING_BODIES[bodyId]?.hasJurisdiction === true;

/** What a body is called, for a row that has no level to show instead. */
export const bodyName = (bodyId) => CREDENTIALING_BODIES[bodyId]?.name ?? null;

/** What this body calls the number on its document, as a dictionary key. */
export const numberLabelKeyFor = (bodyId) => {
  return CREDENTIALING_BODIES[bodyId]?.numberLabelKey ?? 'inputCertificateId';
};
